import type { FunctionComponent } from 'react';
import { useEffect } from 'react';
import { Button, MOBILE_MAX } from '@property.com.au/ui-kit';
import type { ButtonProps } from '@property.com.au/ui-kit';
import styled from 'styled-components';
import { usePropertyPageModalContext } from '../../../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import { trackModalImpression } from '../../../../../analytics/event-tracking/trackModalImpression';
import { useAccountQuery } from '../../../../../hooks/useAccountQuery';
import { useUrlContext } from '../../../../../contexts/UrlContext/UrlContext';
import type { WebLink as IWebLink } from '../../../../../app-kit/components/WebLink/types';
import { WebLink } from '../../../../../app-kit/components/WebLink/WebLink';
import { isNotNullOrUndefinedValue } from '../../../../../helpers/isNotNullOrUndefinedValue';
import { replace } from '@rea-argonaut/link';

const StyledWebLink = styled(WebLink)`
  font-weight: 400;
`;

const StyledButton = styled(Button)`
  font-weight: 400;
  font-size: 0.85rem;
`;

export type GuestCta = {
  link: IWebLink;
};

type MemberCta = {
  button: ButtonProps & {
    text?: string;
  };
  openModalHash: string;
};

const isMemberCta = (cta: GuestCta | MemberCta): cta is MemberCta => 'button' in cta;

export interface UpdatePropertyDetailsHeaderProps {
  cta: GuestCta | MemberCta;
}

const trackUpdatePropertyDetailsModalImpression = () => {
  trackModalImpression({
    user_action: 'opened',
    modal_name: 'update_property_details',
    screen_name: 'property_details',
    modal_is_fullscreen: window.innerWidth <= MOBILE_MAX + 1,
    in_page_source: 'property_page_header_property_features',
  });
};

export const UpdatePropertyDetailsHeader: FunctionComponent<UpdatePropertyDetailsHeaderProps> = ({
  cta,
}) => {
  const { displayModal } = usePropertyPageModalContext();

  const { data } = useAccountQuery();

  const signedIn = isNotNullOrUndefinedValue(data?.account);
  const url = useUrlContext();

  useEffect(() => {
    if (!cta || !isMemberCta(cta)) return;
    if (signedIn && url.hash === cta.openModalHash) {
      trackUpdatePropertyDetailsModalImpression();
      displayModal({ name: 'updatePropertyDetailsModal' });
      replace(url.pathname + url.search);
    }
  }, [cta, signedIn, url.hash, displayModal, url.pathname, url.search]);
  return (
    <>
      {!signedIn && !isMemberCta(cta) && (
        <StyledWebLink
          {...cta.link}
          key={cta.link.key}
          clientSideRouting={false}
          sizeVariant="small"
          rel="noopener nofollow"
        />
      )}
      {isMemberCta(cta) && (
        <StyledButton
          {...cta.button}
          sizeVariant={cta.button.sizeVariant ?? 'small'}
          onClick={() => {
            trackUpdatePropertyDetailsModalImpression();
            displayModal({ name: 'updatePropertyDetailsModal' });
          }}
        >
          {cta.button.text}
        </StyledButton>
      )}
    </>
  );
};
